import { KeyValue } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-camera-dialog',
  templateUrl: './camera-dialog.component.html',
  styleUrls: ['./camera-dialog.component.scss']
})
export class CameraDialogComponent implements AfterViewInit, OnDestroy {
  private _cameraView!: ElementRef<HTMLVideoElement>;
  @ViewChild("cameraView")
  set cameraView(value: ElementRef<HTMLVideoElement>) {
    this._cameraView = value;
    if (this._stream && value) {
      this.cameraView.nativeElement.srcObject = this._stream;
    }
  }
  get cameraView() {
    return this._cameraView;
  }

  private _canvas: HTMLCanvasElement;
  private _context: CanvasRenderingContext2D;
  private _stream!: MediaStream;
  private _imageCapture?: any;
  unsupported = false;
  nodevice = false;
  selectedCamera?: string;
  resolution: string = "hd";
  imageSource?: SafeResourceUrl | null;
  devices = new Array<MediaDeviceInfo>();
  loading: boolean = true;

  readonly RESOLUTIONS: Record<string, { width: { exact: number }; height: { exact: number } }> = {
    fullhd: { width: { exact: 1920 }, height: { exact: 1080 } },
    hd: { width: { exact: 1280 }, height: { exact: 720 } },
    vga: { width: { exact: 640 }, height: { exact: 480 } },
    qvga: { width: { exact: 320 }, height: { exact: 240 } }
  };

  data?: Blob;
  onCapture: () => void = this._onTakeVideoFrame;

  constructor(
    private domSanitizer: DomSanitizer,
    private _self: MatDialogRef<CameraDialogComponent>,
    private _cd: ChangeDetectorRef
  ) {
    this._canvas = document.createElement("canvas");
    this._context = this._canvas.getContext("2d")!;
    if ("ImageCapture" in window) {
      this.onCapture = this._onTakePhoto;
    }
  }

  ngAfterViewInit(): void {
    this.initCamera();
  }

  onVideoMetaDataLoaded() {
    if (this.cameraView && this.cameraView.nativeElement) {
      this._canvas.width = this.cameraView.nativeElement.videoWidth;
      this._canvas.height = this.cameraView.nativeElement.videoHeight;
    }
  }

  async _onTakePhoto() {
    try {
      const setting: any = {
        fillLightMode: "flash",
      };
      if (this.RESOLUTIONS[this.resolution]) {
        setting.imageHeight = this.RESOLUTIONS[this.resolution].height.exact;
        setting.imageWidth = this.RESOLUTIONS[this.resolution].width.exact;
      }
      this.data = await this._imageCapture.takePhoto(setting);
      this.imageSource = this.domSanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(this.data!));
      this._cd.detectChanges();
    } catch {
      this._onTakeVideoFrame();
    }
  }

  private async _onTakeVideoFrame() {
    if (!this.cameraView || !this.cameraView.nativeElement) return;
    this._context.drawImage(this.cameraView.nativeElement, 0, 0, this._canvas.width, this._canvas.height);
    this.data = await new Promise((res) => {
      this._canvas.toBlob((blob) => res(blob!), "image/png", 1);
    });
    if (this.data) {
      this.imageSource = this.domSanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(this.data));
    }
    this._cd.detectChanges();
  }

  onReset() {
    this.imageSource = null;
    this.data = undefined;
    this._cd.detectChanges();
  }

  private _stopAllTrack() {
    if (!this._stream) return;
    for (const track of this._stream.getTracks()) {
      track.stop();
    }
  }

  onDone() {
    this._self.close(this.data);
  }

  originalOrder(_a: any, _b: any) {
    return 0;
  }

  async initCamera() {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        this.unsupported = true;
        this.loading = false;
        this._cd.detectChanges();
        return;
      }
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.devices = devices.filter(device => device.kind === "videoinput");
      this.nodevice = !this.devices || !this.devices.length;
      if (this.nodevice) {
        this.loading = false;
        this._cd.detectChanges();
        return;
      }
      this.selectedCamera = this.devices[0].deviceId;
      await this._getStreamByDeviceID();
    } catch (e) {
      console.error("Camera init error:", e);
      this.nodevice = true;
      this.loading = false;
      this._cd.detectChanges();
    }
  }

  private async _getStreamByDeviceID() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.unsupported = true;
      this.loading = false;
      this._cd.detectChanges();
      return;
    }
    this.loading = true;
    this._stopAllTrack();
    const constraints: MediaStreamConstraints = {
      audio: false,
      video: {
        deviceId: this.selectedCamera ? { exact: this.selectedCamera } : undefined,
        width: this.RESOLUTIONS[this.resolution]?.width,
        height: this.RESOLUTIONS[this.resolution]?.height
      }
    };
    try {
      this._stream = await navigator.mediaDevices.getUserMedia(constraints);
      if ("ImageCapture" in window) {
        this._imageCapture = new (window as any).ImageCapture(this._stream.getVideoTracks()[0]);
      }
      if (this.cameraView && this.cameraView.nativeElement) {
        this.cameraView.nativeElement.srcObject = this._stream;
      }
    } catch (e) {
      console.error("Get stream error:", e);
    } finally {
      this.loading = false;
      this._cd.detectChanges();
    }
  }

  onDeviceSelectionChange(e: string) {
    if (e === this.selectedCamera) return;
    this.selectedCamera = e;
    this._getStreamByDeviceID();
  }

  onResolutionSelectionChange(e: string) {
    if (e === this.resolution) return;
    this.resolution = e;
    this._getStreamByDeviceID();
  }

  ngOnDestroy(): void {
    this._stopAllTrack();
  }
}
